import { type WorkerPendingVisitInvitesResponse } from "@clipboard-health/contract-home-health-api";
import { Title } from "@clipboard-health/ui-components";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format } from "date-fns-tz";
import { useCallback, useMemo, useRef } from "react";

import { GroupedList } from "../../components/GroupedList";
import { ShiftListDateHeader } from "../../Shift/Open/ListDateHeader";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { ShiftInviteAlerts } from "./Alerts";
import { ShiftInviteItemProvider } from "./context/shiftInviteItemContext";
import { ShiftInviteCardContainer } from "./ShiftInviteCardContainer";
import { type ShiftInvite, type ShiftInvitesResponse } from "./types";
import { VisitInviteCard } from "./VisitInviteCard";

const VISIT_INVITES_GROUP_KEY = "0-visit-invites";
const SHIFT_INVITE_GROUP_PREFIX = "1-";

type MixedInviteListItem =
  | { type: "visit-invite"; data: WorkerPendingVisitInvitesResponse["data"][0] }
  | { type: "shift-invite"; data: ShiftInvite };

interface MixedInvitesListProps {
  shiftInvites: ShiftInvitesResponse["data"];
  visitInvites: WorkerPendingVisitInvitesResponse["data"];
}

export function MixedInvitesList(props: MixedInvitesListProps) {
  const { shiftInvites, visitInvites } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();

  const mixedItems: MixedInviteListItem[] = useMemo(() => {
    const visitItems: MixedInviteListItem[] = visitInvites.map((invite) => ({
      type: "visit-invite" as const,
      data: invite,
    }));

    const sortedShiftInvites = [...shiftInvites].sort((a, b) => {
      const dateA = parseISO(a.attributes.shiftDetails.start);
      const dateB = parseISO(b.attributes.shiftDetails.start);
      return dateA.getTime() - dateB.getTime();
    });

    const shiftItems: MixedInviteListItem[] = sortedShiftInvites.map((invite) => ({
      type: "shift-invite" as const,
      data: invite,
    }));

    return [...visitItems, ...shiftItems];
  }, [shiftInvites, visitInvites]);

  const getGroupKey = useCallback(
    (item: MixedInviteListItem): string => {
      if (item.type === "visit-invite") {
        return VISIT_INVITES_GROUP_KEY;
      }

      const dateKey = format(parseISO(item.data.attributes.shiftDetails.start), "yyyy-MM-dd", {
        timeZone,
      });
      return `${SHIFT_INVITE_GROUP_PREFIX}${dateKey}`;
    },
    [timeZone]
  );

  const renderGroupHeader = useCallback(
    (groupKey: string, count: number) => {
      if (groupKey === VISIT_INVITES_GROUP_KEY) {
        return (
          <Title semibold variant="h5" component="h2">
            Visit invites
          </Title>
        );
      }

      const dateIso = groupKey.slice(SHIFT_INVITE_GROUP_PREFIX.length);
      return (
        <ShiftListDateHeader
          key={dateIso}
          label={formatDayOfMonthWithDayName(parseISO(dateIso), { timezone: timeZone })}
          totalShiftsCount={count}
        />
      );
    },
    [timeZone]
  );

  const renderItem = useCallback((item: MixedInviteListItem) => {
    if (item.type === "visit-invite") {
      return <VisitInviteCard key={item.data.id} invite={item.data} />;
    }

    return (
      <ShiftInviteItemProvider key={item.data.id}>
        <ShiftInviteCardContainer shiftInvite={item.data} />
        <ShiftInviteAlerts shiftInvite={item.data} />
      </ShiftInviteItemProvider>
    );
  }, []);

  return (
    <GroupedList
      items={mixedItems}
      groupBy={getGroupKey}
      customScrollParentRef={scrollRef}
      renderGroupHeader={renderGroupHeader}
      renderItem={renderItem}
    />
  );
}
